
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ContactPerson, ContactRole } from "@/lib/types";

export function useContacts() {
  const queryClient = useQueryClient();

  const fetchContacts = async (): Promise<ContactPerson[]> => {
    const { data, error } = await supabase
      .from("contact_persons")
      .select("*")
      .order("name");

    if (error) {
      toast.error(`Error fetching contacts: ${error.message}`);
      throw error;
    }

    return (data || []).map(contact => ({
      id: contact.id,
      name: contact.name,
      email: contact.email,
      phone: contact.phone || undefined,
      role: contact.role as ContactRole,
      created_at: contact.created_at,
      updated_at: contact.updated_at
    }));
  };

  const createContact = async (contact: Omit<ContactPerson, "id">): Promise<ContactPerson> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }
    
    const { data, error } = await supabase
      .from("contact_persons")
      .insert([{
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        role: contact.role,
        user_id: userId,
        created_at: contact.created_at,
        updated_at: contact.updated_at
      }])
      .select()
      .single();

    if (error) {
      toast.error(`Error creating contact: ${error.message}`);
      throw error;
    }

    if (!data) {
      toast.error("Failed to retrieve the created contact");
      throw new Error("Failed to retrieve the created contact");
    }

    toast.success("Contact created successfully");
    return {
      id: data.id,
      name: data.name,
      email: data.email,
      phone: data.phone || undefined,
      role: data.role as ContactRole,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };

  const updateContact = async (contact: Partial<ContactPerson> & { id: string }): Promise<ContactPerson> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }
    
    const updateData: Record<string, any> = {};
    if (contact.name !== undefined) updateData.name = contact.name;
    if (contact.email !== undefined) updateData.email = contact.email;
    if (contact.phone !== undefined) updateData.phone = contact.phone;
    if (contact.role !== undefined) updateData.role = contact.role;
    if (contact.updated_at) updateData.updated_at = contact.updated_at;
    
    const { data, error } = await supabase
      .from("contact_persons")
      .update(updateData)
      .eq('id', contact.id)
      .select()
      .single();

    if (error) {
      toast.error(`Error updating contact: ${error.message}`);
      throw error;
    }

    if (!data) {
      toast.error("Failed to retrieve the updated contact");
      throw new Error("Failed to retrieve the updated contact");
    }

    toast.success("Contact updated successfully");
    return {
      id: data.id,
      name: data.name,
      email: data.email,
      phone: data.phone || undefined,
      role: data.role as ContactRole,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };

  const deleteContact = async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("contact_persons")
      .delete()
      .eq('id', id);

    if (error) {
      toast.error(`Error deleting contact: ${error.message}`);
      throw error;
    }

    toast.success("Contact deleted successfully");
  };

  const getContactById = async (id: string): Promise<ContactPerson> => {
    const { data, error } = await supabase
      .from("contact_persons")
      .select("*")
      .eq('id', id)
      .single();

    if (error) {
      toast.error(`Error fetching contact: ${error.message}`);
      throw error;
    }

    if (!data) {
      toast.error("Contact not found");
      throw new Error("Contact not found");
    }

    return {
      id: data.id,
      name: data.name,
      email: data.email,
      phone: data.phone || undefined,
      role: data.role as ContactRole,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };

  const useContactsQuery = () => useQuery({
    queryKey: ["contacts"],
    queryFn: fetchContacts,
  });

  const useContactQuery = (id: string) => useQuery({
    queryKey: ["contacts", id],
    queryFn: () => getContactById(id),
    enabled: !!id,
  });

  const useCreateContactMutation = () => useMutation({
    mutationFn: createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  const useUpdateContactMutation = () => useMutation({
    mutationFn: updateContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  const useDeleteContactMutation = () => useMutation({
    mutationFn: deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  return {
    useContactsQuery,
    useContactQuery,
    useCreateContactMutation,
    useUpdateContactMutation,
    useDeleteContactMutation,
  };
}
